// src/components/AdminPanel.js
import React, { useEffect, useState, useRef } from "react";
import { supabase } from "../supabaseClient";
import "../styles/admin-panel.css";

export default function AdminPanel() {
  const [recipes, setRecipes] = useState([]);
  const [travels, setTravels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterHighlight, setFilterHighlight] = useState("all");

  const [activeTab, setActiveTab] = useState("recipes");

  // Edit modals
  const [editRecipe, setEditRecipe] = useState(null);
  const [editTravel, setEditTravel] = useState(null);
  const [travelEditTab, setTravelEditTab] = useState("info"); // "info" or "restaurants"
  const [travelRestaurants, setTravelRestaurants] = useState([]);
  const [newRestaurantName, setNewRestaurantName] = useState("");
  const [newRestaurantImage, setNewRestaurantImage] = useState(null);
  const restaurantImageInputRef = useRef(null);

  // New travel
  const [newTravelTitle, setNewTravelTitle] = useState("");
  const [newTravelDesc, setNewTravelDesc] = useState("");
  const [newTravelImages, setNewTravelImages] = useState([]);
  const travelImageInputRef = useRef(null);
  const [newTravelTab, setNewTravelTab] = useState("info"); // "info" or "restaurants"
  const [newTravelRecommendations, setNewTravelRecommendations] = useState([]);
  const [showRecommendationForm, setShowRecommendationForm] = useState(false);
  const [currentRecommendation, setCurrentRecommendation] = useState({
    description: "",
    restaurant: "",
    dish: "",
    imageFile: null
  });
  const newRecommendationImageInputRef = useRef(null);

  // New recipe
  const [newRecipeTitle, setNewRecipeTitle] = useState("");
  const [newRecipeDesc, setNewRecipeDesc] = useState("");
  const [newRecipeIngredients, setNewRecipeIngredients] = useState("");
  const [newRecipeSteps, setNewRecipeSteps] = useState("");
  const [newRecipeCat, setNewRecipeCat] = useState("");
  const [newRecipeType, setNewRecipeType] = useState("slano");
  const [newRecipeHighlight, setNewRecipeHighlight] = useState(false);
  const [newRecipeImages, setNewRecipeImages] = useState([]);
  const [newRecipeImagesIngredients, setNewRecipeImagesIngredients] = useState([]);
  const [newRecipeImagesSteps, setNewRecipeImagesSteps] = useState([]);
  const recipeImageInputRef = useRef(null);
  const ingredientsImageInputRef = useRef(null);
  const stepsImageInputRef = useRef(null);

  // Edit images (arrays of files to add)
  const [editRecipeNewImages, setEditRecipeNewImages] = useState([]);
  const [editTravelNewImages, setEditTravelNewImages] = useState([]);

  /* ------------------------------------------------------ */
  /* LOAD DATA */
  /* ------------------------------------------------------ */
  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data: recipeData, error: recipeError } = await supabase
        .from("recipes")
        .select("*, recipe_images(*)")
        .order("created_at", { ascending: false });

      const { data: travelData, error: travelError } = await supabase
        .from("travels")
        .select("*")
        .order("created_at", { ascending: false });

      // Load travel images separately if table exists
      let travelImagesMap = {};
      try {
        const { data: travelImagesData } = await supabase
          .from("travel_images")
          .select("*");
        
        if (travelImagesData) {
          travelImagesMap = travelImagesData.reduce((acc, img) => {
            if (!acc[img.travel_id]) {
              acc[img.travel_id] = [];
            }
            acc[img.travel_id].push(img);
            return acc;
          }, {});
        }
      } catch (imgErr) {
        // Table doesn't exist yet, that's okay
        console.log("travel_images table not found, using legacy image_url");
      }

      const { data: catData, error: catError } = await supabase
        .from("categories")
        .select("*")
        .order("name");

      if (recipeError) throw recipeError;
      if (travelError) throw travelError;
      if (catError) throw catError;

      setRecipes(recipeData || []);
      
      // Load travel restaurants separately if table exists
      let travelRestaurantsMap = {};
      try {
        const { data: travelRestaurantsData } = await supabase
          .from("travel_restaurants")
          .select("*");
        
        if (travelRestaurantsData) {
          travelRestaurantsMap = travelRestaurantsData.reduce((acc, rest) => {
            if (!acc[rest.travel_id]) {
              acc[rest.travel_id] = [];
            }
            acc[rest.travel_id].push(rest);
            return acc;
          }, {});
        }
      } catch (restErr) {
        // Table doesn't exist yet, that's okay
        console.log("travel_restaurants table not found");
      }

      // Attach travel_images and restaurants to travels
      const travelsWithImages = (travelData || []).map(t => ({
        ...t,
        travel_images: travelImagesMap[t.id] || [],
        restaurants: travelRestaurantsMap[t.id] || []
      }));
      setTravels(travelsWithImages);
      
      setCategories(catData || []);

      if (!newRecipeCat && catData?.length > 0) {
        setNewRecipeCat(catData[0].slug);
      }
    } catch (err) {
      console.error("Error loading data:", err);
      setError("Greška pri učitavanju podataka: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  /* ------------------------------------------------------ */
  /* FILTER RECIPES */
  /* ------------------------------------------------------ */
  const filteredRecipes = recipes.filter((r) => {
    const matchSearch =
      r.title.toLowerCase().includes(search.toLowerCase()) ||
      r.description?.toLowerCase().includes(search.toLowerCase());

    const matchCat = filterCat === "all" ? true : r.category === filterCat;

    const matchType =
      filterType === "all" ? true : (r.taste || r.type) === filterType;

    const matchHighlight =
      filterHighlight === "all"
        ? true
        : filterHighlight === "yes"
        ? r.highlight
        : !r.highlight;

    return matchSearch && matchCat && matchType && matchHighlight;
  });

  /* ------------------------------------------------------ */
  /* DELETE */
  /* ------------------------------------------------------ */
  const deleteRecipe = async (id) => {
    if (!window.confirm("Obrisati recept?")) return;
    setLoading(true);
    try {
      const { error } = await supabase.from("recipes").delete().eq("id", id);
      if (error) throw error;
      await loadData();
    } catch (err) {
      console.error("Error deleting recipe:", err);
      setError("Greška pri brisanju recepta: " + err.message);
      setLoading(false);
    }
  };

  const deleteTravel = async (id) => {
    if (!window.confirm("Obrisati putovanje?")) return;
    setLoading(true);
    try {
      const { error } = await supabase.from("travels").delete().eq("id", id);
      if (error) throw error;
      await loadData();
    } catch (err) {
      console.error("Error deleting travel:", err);
      setError("Greška pri brisanju putovanja: " + err.message);
      setLoading(false);
    }
  };

  /* ------------------------------------------------------ */
  /* DELETE RECIPE IMAGE */
  /* ------------------------------------------------------ */
  const deleteRecipeImage = async (imageId, imageUrl) => {
    if (!window.confirm("Obrisati ovu sliku?")) return;
    setLoading(true);
    try {
      // Delete from database
      const { error: dbError } = await supabase
        .from("recipe_images")
        .delete()
        .eq("id", imageId);

      if (dbError) throw dbError;

      // Extract path from URL and delete from storage
      try {
        const urlParts = imageUrl.split("/");
        const pathIndex = urlParts.findIndex((part) => part === "recipes");
        if (pathIndex !== -1) {
          const path = urlParts.slice(pathIndex + 1).join("/");
          await supabase.storage.from("recipes").remove([path]);
        }
      } catch (storageErr) {
        console.warn("Error deleting from storage:", storageErr);
      }

      await loadData();
    } catch (err) {
      console.error("Error deleting recipe image:", err);
      setError("Greška pri brisanju slike: " + err.message);
      setLoading(false);
    }
  };

  /* ------------------------------------------------------ */
  /* DELETE TRAVEL IMAGE */
  /* ------------------------------------------------------ */
  const deleteTravelImage = async (imageId, imageUrl) => {
    if (!window.confirm("Obrisati ovu sliku?")) return;
    setLoading(true);
    try {
      // Try to delete from travel_images table if it exists
      try {
        const { error: dbError } = await supabase
          .from("travel_images")
          .delete()
          .eq("id", imageId);

        if (dbError && !dbError.message.includes("Could not find")) {
          throw dbError;
        }
      } catch (tableErr) {
        // If table doesn't exist, that's okay - might be legacy image_url
        console.log("travel_images table not available");
      }

      // Extract path from URL and delete from storage
      try {
        const urlParts = imageUrl.split("/");
        const pathIndex = urlParts.findIndex((part) => part === "travels");
        if (pathIndex !== -1) {
          const path = urlParts.slice(pathIndex + 1).join("/");
          await supabase.storage.from("travels").remove([path]);
        }
      } catch (storageErr) {
        console.warn("Error deleting from storage:", storageErr);
      }

      await loadData();
    } catch (err) {
      console.error("Error deleting travel image:", err);
      setError("Greška pri brisanju slike: " + err.message);
      setLoading(false);
    }
  };

  /* ------------------------------------------------------ */
  /* SAVE RECIPE EDIT */
  /* ------------------------------------------------------ */
  const saveRecipeEdit = async () => {
    setLoading(true);
    try {
      // Update recipe data
      const { error } = await supabase
        .from("recipes")
        .update({
          title: editRecipe.title,
          description: editRecipe.description,
          ingredients: editRecipe.ingredients,
          steps: editRecipe.steps,
          category: editRecipe.category,
          taste: editRecipe.taste || editRecipe.type,
          highlight: editRecipe.highlight,
        })
        .eq("id", editRecipe.id);

      if (error) throw error;

      // Upload new images if any
      if (editRecipeNewImages.length > 0) {
        for (const file of editRecipeNewImages) {
          const path = `${editRecipe.id}/${Date.now()}-${file.name}`;

          const { error: uploadErr } = await supabase.storage
            .from("recipes")
            .upload(path, file);

          if (uploadErr) {
            throw new Error("Greška pri učitavanju slike: " + uploadErr.message);
          }

          const { data: urlData } = await supabase.storage
            .from("recipes")
            .getPublicUrl(path);

          const { error: imageError } = await supabase
            .from("recipe_images")
            .insert({
              recipe_id: editRecipe.id,
              image_url: urlData.publicUrl,
            });

          if (imageError) throw imageError;
        }
      }

      setEditRecipeNewImages([]);
      setEditRecipe(null);
      await loadData();
    } catch (err) {
      console.error("Error saving recipe:", err);
      setError("Greška pri spremanju recepta: " + err.message);
      setLoading(false);
    }
  };

  /* ------------------------------------------------------ */
  /* LOAD TRAVEL RESTAURANTS */
  /* ------------------------------------------------------ */
  const loadTravelRestaurants = async (travelId) => {
    try {
      const { data, error } = await supabase
        .from("travel_restaurants")
        .select("*")
        .eq("travel_id", travelId)
        .order("created_at", { ascending: true });

      if (error && !error.message.includes("Could not find")) {
        throw error;
      }

      setTravelRestaurants(data || []);
    } catch (err) {
      console.log("travel_restaurants table not found or error:", err);
      setTravelRestaurants([]);
    }
  };

  /* ------------------------------------------------------ */
  /* ADD RESTAURANT */
  /* ------------------------------------------------------ */
  const addRestaurant = async () => {
    if (!newRestaurantName.trim()) {
      setError("Unesite naziv restorana");
      return;
    }

    if (travelRestaurants.length >= 10) {
      setError("Maksimalno 10 preporuka restorana");
      return;
    }

    if (!newRestaurantImage) {
      setError("Odaberite fotografiju restorana");
      return;
    }

    setLoading(true);
    try {
      // Upload restaurant image
      const path = `restaurants/${editTravel.id}/${Date.now()}-${newRestaurantImage.name}`;
      const { error: uploadErr } = await supabase.storage
        .from("travels")
        .upload(path, newRestaurantImage);

      if (uploadErr) {
        throw new Error("Greška pri učitavanju slike: " + uploadErr.message);
      }

      const { data: urlData } = await supabase.storage
        .from("travels")
        .getPublicUrl(path);

      // Insert restaurant record
      const { error: insertError } = await supabase
        .from("travel_restaurants")
        .insert({
          travel_id: editTravel.id,
          name: newRestaurantName.trim(),
          image_url: urlData.publicUrl,
        });

      if (insertError && !insertError.message.includes("Could not find")) {
        throw insertError;
      }

      setNewRestaurantName("");
      setNewRestaurantImage(null);
      if (restaurantImageInputRef.current) {
        restaurantImageInputRef.current.value = "";
      }
      await loadTravelRestaurants(editTravel.id);
    } catch (err) {
      console.error("Error adding restaurant:", err);
      setError("Greška pri dodavanju restorana: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  /* ------------------------------------------------------ */
  /* DELETE RESTAURANT */
  /* ------------------------------------------------------ */
  const deleteRestaurant = async (restaurantId, imageUrl) => {
    if (!window.confirm("Obrisati ovu preporuku restorana?")) return;
    setLoading(true);
    try {
      // Delete from database
      const { error: dbError } = await supabase
        .from("travel_restaurants")
        .delete()
        .eq("id", restaurantId);

      if (dbError && !dbError.message.includes("Could not find")) {
        throw dbError;
      }

      // Delete from storage
      try {
        const urlParts = imageUrl.split("/");
        const pathIndex = urlParts.findIndex((part) => part === "restaurants");
        if (pathIndex !== -1) {
          const path = urlParts.slice(pathIndex + 1).join("/");
          await supabase.storage.from("travels").remove([path]);
        }
      } catch (storageErr) {
        console.warn("Error deleting from storage:", storageErr);
      }

      await loadTravelRestaurants(editTravel.id);
    } catch (err) {
      console.error("Error deleting restaurant:", err);
      setError("Greška pri brisanju restorana: " + err.message);
      setLoading(false);
    }
  };

  /* ------------------------------------------------------ */
  /* SAVE TRAVEL EDIT */
  /* ------------------------------------------------------ */
  const saveTravelEdit = async () => {
    setLoading(true);
    try {
      // Update travel data
      const { error } = await supabase
        .from("travels")
        .update({
          title: editTravel.title,
          description: editTravel.description || null,
        })
        .eq("id", editTravel.id);

      if (error) throw error;

      // Upload new images if any
      if (editTravelNewImages.length > 0) {
        // Upload first image to legacy image_url field
        const firstFile = editTravelNewImages[0];
        const path = `${editTravel.id}/${Date.now()}-${firstFile.name}`;

        const { error: uploadErr } = await supabase.storage
          .from("travels")
          .upload(path, firstFile);

        if (uploadErr) {
          throw new Error("Greška pri učitavanju slike: " + uploadErr.message);
        }

        const { data: urlData } = await supabase.storage
          .from("travels")
          .getPublicUrl(path);

        // Update travel with first image URL (legacy support)
        const { error: updateError } = await supabase
          .from("travels")
          .update({ image_url: urlData.publicUrl })
          .eq("id", editTravel.id);

        if (updateError) throw updateError;

        // Try to insert into travel_images if table exists (for multiple images)
        if (editTravelNewImages.length > 1) {
          try {
            for (let i = 0; i < editTravelNewImages.length; i++) {
              const file = editTravelNewImages[i];
              const imgPath = `${editTravel.id}/${Date.now()}-${i}-${file.name}`;

              const { error: uploadErr2 } = await supabase.storage
                .from("travels")
                .upload(imgPath, file);

              if (!uploadErr2) {
                const { data: urlData2 } = await supabase.storage
                  .from("travels")
                  .getPublicUrl(imgPath);

                const { error: imageError } = await supabase
                  .from("travel_images")
                  .insert({
                    travel_id: editTravel.id,
                    image_url: urlData2.publicUrl,
                  });

                // If table doesn't exist, just continue (we already have image_url)
                if (imageError && !imageError.message.includes("Could not find")) {
                  console.warn("Could not insert into travel_images:", imageError);
                }
              }
            }
          } catch (err) {
            // If travel_images table doesn't exist, that's okay - we have image_url
            console.log("travel_images table not available, using legacy image_url only");
          }
        }
      }

      setEditTravelNewImages([]);
      setEditTravel(null);
      setTravelEditTab("info");
      setTravelRestaurants([]);
      await loadData();
    } catch (err) {
      console.error("Error saving travel:", err);
      setError("Greška pri spremanju putovanja: " + err.message);
      setLoading(false);
    }
  };

  /* ------------------------------------------------------ */
  /* CREATE TRAVEL */
  /* NOTE: If you want to add 'description' field, add it to Supabase:
   * 1. Go to Supabase Dashboard > Table Editor > travels table
   * 2. Add new column: name='description', type='text', nullable=true
   * 3. Then uncomment description fields in this component
   * ------------------------------------------------------ */
  const createTravel = async (e) => {
    if (e) {
      e.preventDefault();
    }
    console.log("=== createTravel CALLED ===");
    console.log("Form submitted with:", { 
      title: newTravelTitle, 
      description: newTravelDesc,
      imagesCount: newTravelImages.length,
      recommendationsCount: newTravelRecommendations.length,
      recommendations: newTravelRecommendations
    });
    
    if (newTravelRecommendations.length > 0) {
      console.log("Recommendations details:", newTravelRecommendations.map(r => ({
        hasImage: !!r.imageFile,
        restaurant: r.restaurant,
        description: r.description,
        dish: r.dish
      })));
    }
    
    // Validation
    if (!newTravelTitle.trim()) {
      console.log("Validation failed: No title");
      setError("Unesite naslov putovanja");
      return;
    }

    console.log("Validation passed, starting creation...");
    setLoading(true);
    setError(null);

    console.log("Creating travel:", { title: newTravelTitle, hasImages: newTravelImages.length > 0 });

    try {
      // First create the travel
      const { data: createdTravel, error: insertError } = await supabase
        .from("travels")
        .insert({
          title: newTravelTitle.trim(),
          description: newTravelDesc?.trim() || null,
        })
        .select()
        .single();

      if (insertError) {
        console.error("Error inserting travel:", insertError);
        throw insertError;
      }

      if (!createdTravel) {
        throw new Error("Putovanje nije kreirano");
      }

      console.log("Travel created successfully:", createdTravel.id);

      // Create recommendations if any
      console.log("Creating recommendations, count:", newTravelRecommendations.length);
      if (newTravelRecommendations.length > 0 && createdTravel) {
        try {
          for (let i = 0; i < newTravelRecommendations.length; i++) {
            const recommendation = newTravelRecommendations[i];
            console.log(`Processing recommendation ${i + 1}:`, recommendation);
            
            if (recommendation.imageFile) {
              // Upload recommendation image
              const recPath = `restaurants/${createdTravel.id}/${Date.now()}-${i}-${recommendation.imageFile.name}`;
              console.log("Uploading image to path:", recPath);
              
              const { error: uploadRecErr } = await supabase.storage
                .from("travels")
                .upload(recPath, recommendation.imageFile);

              if (uploadRecErr) {
                console.error("Error uploading recommendation image:", uploadRecErr);
                setError(`Greška pri učitavanju slike preporuke ${i + 1}: ${uploadRecErr.message}`);
                continue;
              }

              const { data: recUrlData } = await supabase.storage
                .from("travels")
                .getPublicUrl(recPath);

              console.log("Image uploaded, URL:", recUrlData.publicUrl);

              // Insert recommendation record
              const insertData = {
                travel_id: createdTravel.id,
                name: recommendation.restaurant || "",
                image_url: recUrlData.publicUrl,
              };
              
              // Try to add description and dish if columns exist
              if (recommendation.description) {
                insertData.description = recommendation.description;
              }
              if (recommendation.dish) {
                insertData.dish = recommendation.dish;
              }
              
              console.log("Inserting recommendation data:", insertData);
              
              const { data: insertedRec, error: recInsertError } = await supabase
                .from("travel_restaurants")
                .insert(insertData)
                .select();

              if (recInsertError) {
                console.error("Error inserting recommendation:", recInsertError);
                setError(`Greška pri spremanju preporuke ${i + 1}: ${recInsertError.message}`);
              } else {
                console.log("Recommendation inserted successfully:", insertedRec);
              }
            } else {
              console.warn(`Recommendation ${i + 1} has no image file, skipping`);
            }
          }
          console.log("All recommendations processed");
        } catch (recErr) {
          console.error("Error creating recommendations:", recErr);
          setError("Greška pri kreiranju preporuka: " + recErr.message);
        }
      } else {
        console.log("No recommendations to create");
      }

      // Then upload images if provided
      if (newTravelImages.length > 0 && createdTravel) {
        try {
          // Upload first image to legacy image_url field
          const firstFile = newTravelImages[0];
          const path = `${createdTravel.id}/${Date.now()}-${firstFile.name}`;

          const { error: uploadErr } = await supabase.storage
            .from("travels")
            .upload(path, firstFile);

          if (uploadErr) {
            console.error("Error uploading image:", uploadErr);
            // Don't throw - continue without image, but log the error
            console.warn("Putovanje je kreirano, ali slika nije učitana:", uploadErr.message);
          } else {

            const { data: urlData } = await supabase.storage
              .from("travels")
              .getPublicUrl(path);

            // Update travel with first image URL (legacy support)
            const { error: updateError } = await supabase
              .from("travels")
              .update({ image_url: urlData.publicUrl })
              .eq("id", createdTravel.id);

            if (updateError) {
              console.warn("Error updating travel with image URL:", updateError);
            }
          }

          // Try to insert into travel_images if table exists (for multiple images)
          if (newTravelImages.length > 1) {
            try {
              for (let i = 0; i < newTravelImages.length; i++) {
                const file = newTravelImages[i];
                const imgPath = `${createdTravel.id}/${Date.now()}-${i}-${file.name}`;

                const { error: uploadErr2 } = await supabase.storage
                  .from("travels")
                  .upload(imgPath, file);

                if (!uploadErr2) {
                  const { data: urlData2 } = await supabase.storage
                    .from("travels")
                    .getPublicUrl(imgPath);

                  const { error: imageError } = await supabase
                    .from("travel_images")
                    .insert({
                      travel_id: createdTravel.id,
                      image_url: urlData2.publicUrl,
                    });

                  // If table doesn't exist, just continue (we already have image_url)
                  if (imageError && !imageError.message.includes("Could not find")) {
                    console.warn("Could not insert into travel_images:", imageError);
                  }
                }
              }
            } catch (err) {
              // If travel_images table doesn't exist, that's okay - we have image_url
              console.log("travel_images table not available, using legacy image_url only");
            }
          }
        } catch (imgErr) {
          console.warn("Error processing images:", imgErr);
          // Continue - travel is created, images are optional
        }
      }

      setNewTravelTitle("");
      setNewTravelDesc("");
      setNewTravelImages([]);
      setNewTravelRecommendations([]);
      setNewTravelTab("info");
      setShowRecommendationForm(false);
      setCurrentRecommendation({
        description: "",
        restaurant: "",
        dish: "",
        imageFile: null
      });
      if (travelImageInputRef.current) {
        travelImageInputRef.current.value = "";
      }
      if (newRecommendationImageInputRef.current) {
        newRecommendationImageInputRef.current.value = "";
      }

      setLoading(false);
      await loadData();
    } catch (err) {
      console.error("Error creating travel:", err);
      setError("Greška pri kreiranju putovanja: " + (err.message || "Nepoznata greška"));
      setLoading(false);
    }
  };

  /* ------------------------------------------------------ */
  /* CREATE RECIPE */
  /* ------------------------------------------------------ */
  const createRecipe = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // First create the recipe
      const { data: createdRecipe, error: insertError } = await supabase
        .from("recipes")
        .insert({
          title: newRecipeTitle,
          description: newRecipeDesc,
          ingredients: newRecipeIngredients,
          steps: newRecipeSteps,
          category: newRecipeCat,
          taste: newRecipeType, // SLANO / SLATKO (koristi 'taste' kolonu)
          highlight: newRecipeHighlight,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Helper function to upload images with section
      const uploadImagesWithSection = async (files, section) => {
        for (const file of files) {
          const path = `${createdRecipe.id}/${Date.now()}-${file.name}`;

          const { error: uploadErr } = await supabase.storage
            .from("recipes")
            .upload(path, file);

          if (uploadErr) {
            throw new Error("Greška pri učitavanju slike: " + uploadErr.message);
          }

          const { data: urlData } = await supabase.storage
            .from("recipes")
            .getPublicUrl(path);

          // Insert image record with section
          const insertData = {
            recipe_id: createdRecipe.id,
            image_url: urlData.publicUrl,
          };
          
          // Add section if provided (ingredients or steps)
          if (section) {
            insertData.section = section;
          }

          const { error: imageError } = await supabase
            .from("recipe_images")
            .insert(insertData);
          if (imageError) throw imageError;
        }
      };

      // Upload all images with their sections
      if (createdRecipe) {
        // Main images (no section)
        if (newRecipeImages.length > 0) {
          await uploadImagesWithSection(newRecipeImages, null);
        }
        
        // Images for ingredients section
        if (newRecipeImagesIngredients.length > 0) {
          await uploadImagesWithSection(newRecipeImagesIngredients, "ingredients");
        }
        
        // Images for steps section
        if (newRecipeImagesSteps.length > 0) {
          console.log("Uploading steps images, count:", newRecipeImagesSteps.length);
          await uploadImagesWithSection(newRecipeImagesSteps, "steps");
          console.log("Steps images uploaded successfully");
        } else {
          console.log("No steps images to upload");
        }
      }

      setNewRecipeTitle("");
      setNewRecipeDesc("");
      setNewRecipeIngredients("");
      setNewRecipeSteps("");
      setNewRecipeCat(categories[0]?.slug || "");
      setNewRecipeType("slano");
      setNewRecipeHighlight(false);
      setNewRecipeImages([]);
      setNewRecipeImagesIngredients([]);
      setNewRecipeImagesSteps([]);
      if (recipeImageInputRef.current) {
        recipeImageInputRef.current.value = "";
      }
      if (ingredientsImageInputRef.current) {
        ingredientsImageInputRef.current.value = "";
      }
      if (stepsImageInputRef.current) {
        stepsImageInputRef.current.value = "";
      }

      await loadData();
    } catch (err) {
      console.error("Error creating recipe:", err);
      setError("Greška pri kreiranju recepta: " + err.message);
      setLoading(false);
    }
  };

  /* ------------------------------------------------------ */
  /* RENDER */
  /* ------------------------------------------------------ */

  return (
    <div className="ap-wrap">
      <h1 className="ap-title">Admin Panel</h1>

      {error && (
        <div
          className="ap-card"
          style={{
            background: "#fee",
            border: "1px solid #fcc",
            marginBottom: "20px",
          }}
        >
          <p style={{ color: "#c33", margin: 0 }}>{error}</p>
          <button
            className="ap-btn small"
            onClick={() => setError(null)}
            style={{ marginTop: "10px" }}
          >
            Zatvori
          </button>
        </div>
      )}

      {loading && (
        <div
          className="ap-card"
          style={{ textAlign: "center", marginBottom: "20px" }}
        >
          <p>Učitavanje...</p>
        </div>
      )}

      <button
        className="ap-btn ghost"
        style={{ marginBottom: "20px" }}
        onClick={async () => {
          await supabase.auth.signOut();
          localStorage.removeItem("sb_session");
          window.location.href = "/prijava";
        }}
      >
        🚪 Odjava
      </button>

      {/* TABS */}
      <div className="ap-tabs">
        <button
          className={activeTab === "recipes" ? "active" : ""}
          onClick={() => setActiveTab("recipes")}
        >
          📚 Recepti
        </button>

        <button
          className={activeTab === "travels" ? "active" : ""}
          onClick={() => setActiveTab("travels")}
        >
          🌍 Putovanja
        </button>
      </div>

      {/* ================================================ */}
      {/* RECEPTI */}
      {/* ================================================ */}
      {activeTab === "recipes" && (
        <>
          {/* SEARCH + FILTER */}
          <div className="ap-card ap-toolbar">
            <input
              className="ap-input"
              placeholder="🔍 Pretraži recepte..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            {/* FILTER TYPE */}
            <select
              className="ap-input"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="all">Svi tipovi</option>
              <option value="slano">Slano</option>
              <option value="slatko">Slatko</option>
            </select>

            {/* FILTER CATEGORY */}
            <select
              className="ap-input"
              value={filterCat}
              onChange={(e) => setFilterCat(e.target.value)}
            >
              <option value="all">Sve kategorije</option>

              {categories.map((c) => (
                <option value={c.slug} key={c.id}>
                  {c.name} ({c.type === "slatko" ? "🍰 Slatko" : "🥖 Slano"})
                </option>
              ))}
            </select>

            {/* FILTER HIGHLIGHT */}
            <select
              className="ap-input"
              value={filterHighlight}
              onChange={(e) => setFilterHighlight(e.target.value)}
            >
              <option value="all">Sve</option>
              <option value="yes">Istaknuti</option>
              <option value="no">Neistaknuti</option>
            </select>
          </div>

          {/* ADD NEW RECIPE */}
          <div className="ap-card" style={{ marginTop: "20px" }}>
            <h3>Dodaj novi recept</h3>

            <form className="ap-form" onSubmit={createRecipe}>
              {/* TYPE */}
              <label>
                Tip recepta
                <select
                  className="ap-input"
                  value={newRecipeType}
                  onChange={(e) => {
                    const newType = e.target.value;
                    setNewRecipeType(newType);
                    // Reset category to first available category of selected type
                    const availableCats = categories.filter((c) => c.type === newType);
                    if (availableCats.length > 0) {
                      setNewRecipeCat(availableCats[0].slug);
                    }
                  }}
                >
                  <option value="slano">Slano</option>
                  <option value="slatko">Slatko</option>
                </select>
              </label>

              {/* CATEGORY */}
              <label>
                Kategorija
                <select
                  className="ap-input"
                  value={newRecipeCat}
                  onChange={(e) => setNewRecipeCat(e.target.value)}
                >
                  {categories
                    .filter((c) => c.type === newRecipeType)
                    .map((c) => (
                      <option key={c.id} value={c.slug}>
                        {c.name} ({c.type === "slatko" ? "🍰 Slatko" : "🥖 Slano"})
                      </option>
                    ))}
                </select>
              </label>

              <label>
                Naslov recepta
                <input
                  className="ap-input"
                  value={newRecipeTitle}
                  onChange={(e) => setNewRecipeTitle(e.target.value)}
                  required
                />
              </label>

              <label>
                Opis
                <textarea
                  className="ap-input"
                  value={newRecipeDesc}
                  onChange={(e) => setNewRecipeDesc(e.target.value)}
                />
              </label>

              <label className="ap-check">
                <input
                  type="checkbox"
                  checked={newRecipeHighlight}
                  onChange={(e) => setNewRecipeHighlight(e.target.checked)}
                />
                Istaknuto
              </label>

              {/* Button za fotografiju iznad sastojaka */}
              <div style={{ marginBottom: "12px" }}>
                <button
                  type="button"
                  className="ap-btn"
                  style={{ width: "100%" }}
                  onClick={() => ingredientsImageInputRef.current?.click()}
                >
                  📷 Dodaj fotografiju (ispod sastojaka)
                </button>
                <input
                  ref={ingredientsImageInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setNewRecipeImagesIngredients([...newRecipeImagesIngredients, ...files]);
                  }}
                />
              </div>

              <label>
                Sastojci
                <textarea
                  className="ap-input"
                  value={newRecipeIngredients}
                  onChange={(e) => setNewRecipeIngredients(e.target.value)}
                />
              </label>

              {/* Button za fotografiju ispod koraka pripreme */}
              <div style={{ marginBottom: "12px", marginTop: "12px" }}>
                <button
                  type="button"
                  className="ap-btn"
                  style={{ width: "100%" }}
                  onClick={() => stepsImageInputRef.current?.click()}
                >
                  📷 Dodaj fotografiju (ispod koraka pripreme)
                </button>
                <input
                  ref={stepsImageInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    console.log("Adding steps images:", files);
                    setNewRecipeImagesSteps([...newRecipeImagesSteps, ...files]);
                    console.log("Steps images state after update:", [...newRecipeImagesSteps, ...files]);
                  }}
                />
              </div>

              <label>
                Koraci pripreme
                <textarea
                  className="ap-input"
                  value={newRecipeSteps}
                  onChange={(e) => setNewRecipeSteps(e.target.value)}
                />
              </label>

              <label>
                Slike recepta (može više)
                <input
                  ref={recipeImageInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) =>
                    setNewRecipeImages(Array.from(e.target.files || []))
                  }
                />
                {newRecipeImages.length > 0 && (
                  <span style={{ fontSize: "0.9rem", color: "var(--muted)", marginTop: "4px" }}>
                    Odabrano: {newRecipeImages.length} slika
                  </span>
                )}
              </label>

              <button className="ap-btn">Dodaj recept</button>
            </form>
          </div>

          {/* RECIPE GRID */}
          <div className="ap-grid">
            {filteredRecipes.map((r) => (
              <div key={r.id} className="ap-card ap-item">
                <div className="thumb">
                  {r.recipe_images?.[0]?.image_url ? (
                    <img src={r.recipe_images[0].image_url} alt={r.title} />
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#999" }}>
                      Nema slike
                    </div>
                  )}
                  {r.recipe_images?.length > 1 && (
                    <div style={{ position: "absolute", top: "8px", right: "8px", background: "rgba(0,0,0,0.7)", color: "white", padding: "4px 8px", borderRadius: "4px", fontSize: "0.8rem" }}>
                      +{r.recipe_images.length - 1}
                    </div>
                  )}
                </div>

                <h3>{r.title}</h3>
                <p className="muted">
                  {r.taste || r.type || "N/A"} • {r.category}
                </p>

                <div className="ap-flex-gap">
                  <button
                    className="ap-btn small"
                    onClick={() => {
                      setEditRecipe(r);
                      setEditRecipeNewImages([]);
                    }}
                  >
                    ✎ Uredi
                  </button>

                  <button
                    className="ap-btn ghost small"
                    onClick={async () => {
                      try {
                        const { error } = await supabase
                          .from("recipes")
                          .update({ highlight: !r.highlight })
                          .eq("id", r.id);
                        if (error) throw error;
                        await loadData();
                      } catch (err) {
                        console.error("Error toggling highlight:", err);
                        setError(
                          "Greška pri promjeni istaknutosti: " + err.message
                        );
                      }
                    }}
                  >
                    {r.highlight ? "⭐ Makni istaknuto" : "☆ Istakni"}
                  </button>

                  <button
                    className="ap-btn danger small"
                    onClick={() => deleteRecipe(r.id)}
                  >
                    ❌ Obriši
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ================================================ */}
      {/* PUTOVANJA */}
      {/* ================================================ */}
      {activeTab === "travels" && (
        <>
          <div className="ap-card">
            <h3>Dodaj putovanje</h3>

            <form onSubmit={createTravel} className="ap-form">
              <label>
                Naslov *
                <input
                  className="ap-input"
                  value={newTravelTitle}
                  onChange={(e) => setNewTravelTitle(e.target.value)}
                  required
                  placeholder="Unesite naslov putovanja"
                />
              </label>

              <label>
                Opis putovanja
                <textarea
                  className="ap-input"
                  value={newTravelDesc}
                  onChange={(e) => setNewTravelDesc(e.target.value)}
                />
              </label>

              {/* PREPORUKE SEKCIJA */}
              <div style={{ marginTop: "20px" }}>
                <h4 style={{ marginBottom: "20px" }}>🍽️ Preporuke ({newTravelRecommendations.length}/10)</h4>
                
                {/* POSTOJEĆE PREPORUKE */}
                {newTravelRecommendations.map((rec, index) => (
                  <div key={rec.id} style={{ marginBottom: "20px", padding: "16px", border: "1px solid var(--border)", borderRadius: "8px", background: "#fff" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                      <h5 style={{ margin: 0 }}>Preporuka {index + 1}</h5>
                      <button
                        type="button"
                        className="ap-btn danger small"
                        onClick={() => {
                          setNewTravelRecommendations(newTravelRecommendations.filter((r) => r.id !== rec.id));
                        }}
                      >
                        Obriši
                      </button>
                    </div>
                    {rec.imageFile && (
                      <img
                        src={URL.createObjectURL(rec.imageFile)}
                        alt="Preporuka"
                        style={{
                          width: "100%",
                          maxHeight: "200px",
                          objectFit: "cover",
                          borderRadius: "8px",
                          marginBottom: "12px"
                        }}
                      />
                    )}
                    {rec.description && (
                      <p style={{ margin: "0 0 8px 0", fontSize: "0.95rem" }}><strong>Opis:</strong> {rec.description}</p>
                    )}
                    {rec.restaurant && (
                      <p style={{ margin: "0 0 8px 0", fontSize: "0.95rem" }}><strong>Restoran:</strong> {rec.restaurant}</p>
                    )}
                    {rec.dish && (
                      <p style={{ margin: "0 0 8px 0", fontSize: "0.95rem" }}><strong>Jelo:</strong> {rec.dish}</p>
                    )}
                  </div>
                ))}

                {/* PLUS BUTTON ZA DODAVANJE NOVE PREPORUKE */}
                {newTravelRecommendations.length < 10 && !showRecommendationForm && (
                  <div 
                    style={{ 
                      marginBottom: "24px", 
                      padding: "24px", 
                      background: "var(--bg-light)", 
                      borderRadius: "8px", 
                      border: "2px dashed var(--border)",
                      textAlign: "center",
                      cursor: "pointer",
                      transition: "all 0.2s"
                    }}
                    onClick={() => setShowRecommendationForm(true)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#1f6074";
                      e.currentTarget.style.background = "#f0f8fa";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "var(--border)";
                      e.currentTarget.style.background = "var(--bg-light)";
                    }}
                  >
                    <div style={{ fontSize: "3rem", color: "var(--muted)", marginBottom: "12px" }}>➕</div>
                    <div style={{ color: "var(--muted)", fontSize: "1rem", fontWeight: "600" }}>
                      Dodaj preporuku {newTravelRecommendations.length + 1}
                    </div>
                    <div style={{ color: "var(--muted)", fontSize: "0.85rem", marginTop: "4px" }}>
                      Klikni za dodavanje nove preporuke
                    </div>
                  </div>
                )}

                {/* FORMA ZA DODAVANJE NOVE PREPORUKE */}
                {newTravelRecommendations.length < 10 && showRecommendationForm && (
                  <div style={{ marginBottom: "24px", padding: "16px", background: "var(--bg-light)", borderRadius: "8px", border: "2px dashed var(--border)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                      <h5 style={{ margin: 0 }}>
                        ➕ Preporuka {newTravelRecommendations.length + 1}
                      </h5>
                      <button
                        type="button"
                        className="ap-btn ghost small"
                        onClick={() => {
                          setShowRecommendationForm(false);
                          setCurrentRecommendation({
                            description: "",
                            restaurant: "",
                            dish: "",
                            imageFile: null
                          });
                          if (newRecommendationImageInputRef.current) {
                            newRecommendationImageInputRef.current.value = "";
                          }
                        }}
                      >
                        ✕ Odustani
                      </button>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                      <label>
                        Opis preporuke
                        <textarea
                          className="ap-input"
                          value={currentRecommendation.description}
                          onChange={(e) => setCurrentRecommendation({ ...currentRecommendation, description: e.target.value })}
                          placeholder="Unesite opis preporuke"
                          rows={3}
                        />
                      </label>
                      <label>
                        Restoran
                        <input
                          className="ap-input"
                          value={currentRecommendation.restaurant}
                          onChange={(e) => setCurrentRecommendation({ ...currentRecommendation, restaurant: e.target.value })}
                          placeholder="Unesite naziv restorana"
                        />
                      </label>
                      <label>
                        Jelo
                        <input
                          className="ap-input"
                          value={currentRecommendation.dish}
                          onChange={(e) => setCurrentRecommendation({ ...currentRecommendation, dish: e.target.value })}
                          placeholder="Unesite preporučeno jelo"
                        />
                      </label>
                      <label>
                        Fotografija
                        <input
                          ref={newRecommendationImageInputRef}
                          type="file"
                          accept="image/*"
                          onChange={(e) => setCurrentRecommendation({ ...currentRecommendation, imageFile: e.target.files?.[0] || null })}
                        />
                        {currentRecommendation.imageFile && (
                          <span style={{ fontSize: "0.9rem", color: "var(--muted)", marginTop: "4px", display: "block" }}>
                            Odabrano: {currentRecommendation.imageFile.name}
                          </span>
                        )}
                      </label>
                      <button
                        type="button"
                        className="ap-btn"
                        onClick={() => {
                          if (!currentRecommendation.description.trim() && !currentRecommendation.restaurant.trim() && !currentRecommendation.dish.trim()) {
                            setError("Unesite barem jedan podatak (opis, restoran ili jelo)");
                            return;
                          }
                          if (!currentRecommendation.imageFile) {
                            setError("Odaberite fotografiju");
                            return;
                          }
                          if (newTravelRecommendations.length >= 10) {
                            setError("Maksimalno 10 preporuka");
                            return;
                          }
                          const newRec = {
                            id: Date.now(),
                            ...currentRecommendation
                          };
                          console.log("Adding recommendation to state:", newRec);
                          console.log("Current recommendations count:", newTravelRecommendations.length);
                          
                          setNewTravelRecommendations([
                            ...newTravelRecommendations,
                            newRec
                          ]);
                          
                          console.log("Recommendation added. New count will be:", newTravelRecommendations.length + 1);
                          
                          setCurrentRecommendation({
                            description: "",
                            restaurant: "",
                            dish: "",
                            imageFile: null
                          });
                          setShowRecommendationForm(false);
                          if (newRecommendationImageInputRef.current) {
                            newRecommendationImageInputRef.current.value = "";
                          }
                          setError(null);
                        }}
                        disabled={newTravelRecommendations.length >= 10 || (!currentRecommendation.description.trim() && !currentRecommendation.restaurant.trim() && !currentRecommendation.dish.trim()) || !currentRecommendation.imageFile}
                      >
                        ➕ Dodaj preporuku {newTravelRecommendations.length + 1}
                      </button>
                    </div>
                  </div>
                )}

                {newTravelRecommendations.length >= 10 && (
                  <p style={{ color: "#c33", fontSize: "0.9rem", padding: "12px", background: "#fee", borderRadius: "8px" }}>
                    Dosegnut je maksimum od 10 preporuka
                  </p>
                )}
              </div>

              <label>
                Slike (može više)
                <input
                  ref={travelImageInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) =>
                    setNewTravelImages(Array.from(e.target.files || []))
                  }
                />
                {newTravelImages.length > 0 && (
                  <span style={{ fontSize: "0.9rem", color: "var(--muted)", marginTop: "4px" }}>
                    Odabrano: {newTravelImages.length} slika
                  </span>
                )}
              </label>

              <button 
                className="ap-btn" 
                type="submit"
                disabled={loading || !newTravelTitle.trim()}
                style={{ marginTop: "20px" }}
                onClick={(e) => {
                  console.log("Button clicked!", {
                    loading,
                    hasTitle: !!newTravelTitle.trim(),
                    title: newTravelTitle,
                    disabled: loading || !newTravelTitle.trim()
                  });
                  if (!newTravelTitle.trim()) {
                    e.preventDefault();
                    setError("Unesite naslov putovanja");
                    return false;
                  }
                }}
              >
                {loading ? "Dodavanje..." : "Dodaj putovanje"}
              </button>
            </form>
          </div>

          <div className="ap-grid">
            {travels.map((t) => (
              <div key={t.id} className="ap-card ap-item">
                <div className="thumb">
                  {t.travel_images?.[0]?.image_url || t.image_url ? (
                    <img src={t.travel_images?.[0]?.image_url || t.image_url} alt={t.title} />
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#999" }}>
                      Nema slike
                    </div>
                  )}
                  {t.travel_images?.length > 1 && (
                    <div style={{ position: "absolute", top: "8px", right: "8px", background: "rgba(0,0,0,0.7)", color: "white", padding: "4px 8px", borderRadius: "4px", fontSize: "0.8rem" }}>
                      +{t.travel_images.length - 1}
                    </div>
                  )}
                </div>

                <h3>{t.title}</h3>
                {t.description && <p className="muted">{t.description}</p>}

                <div className="ap-flex-gap">
                  <button
                    className="ap-btn small"
                    onClick={async () => {
                      setEditTravel(t);
                      setEditTravelNewImages([]);
                      setTravelEditTab("info");
                      await loadTravelRestaurants(t.id);
                    }}
                  >
                    ✎ Uredi
                  </button>

                  <button
                    className="ap-btn danger small"
                    onClick={() => deleteTravel(t.id)}
                  >
                    ❌ Obriši
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ================================================ */}
      {/* MODALS */}
      {/* ================================================ */}

      {/* RECIPE MODAL */}
      {editRecipe && (
        <div className="ap-modal">
          <div className="ap-modal-body ap-card">
            <div className="ap-modal-head">
              <h2>Uredi recept</h2>
              <button className="ap-x" onClick={() => setEditRecipe(null)}>
                ✕
              </button>
            </div>

            <div className="ap-form ap-grid-2">
              <label>
                Tip recepta
                <select
                  className="ap-input"
                  value={editRecipe.taste || editRecipe.type || "slano"}
                  onChange={(e) =>
                    setEditRecipe({
                      ...editRecipe,
                      taste: e.target.value,
                      type: e.target.value,
                    })
                  }
                >
                  <option value="slano">Slano</option>
                  <option value="slatko">Slatko</option>
                </select>
              </label>

              <label>
                Kategorija
                <select
                  className="ap-input"
                  value={editRecipe.category}
                  onChange={(e) =>
                    setEditRecipe({ ...editRecipe, category: e.target.value })
                  }
                >
                  {categories
                    .filter((c) => c.type === (editRecipe.taste || editRecipe.type || "slano"))
                    .map((c) => (
                      <option key={c.id} value={c.slug}>
                        {c.name} ({c.type === "slatko" ? "🍰 Slatko" : "🥖 Slano"})
                      </option>
                    ))}
                </select>
              </label>

              <label className="full">
                Naslov
                <input
                  className="ap-input"
                  value={editRecipe.title}
                  onChange={(e) =>
                    setEditRecipe({ ...editRecipe, title: e.target.value })
                  }
                />
              </label>

              <label className="full">
                Opis
                <textarea
                  className="ap-input"
                  value={editRecipe.description}
                  onChange={(e) =>
                    setEditRecipe({
                      ...editRecipe,
                      description: e.target.value,
                    })
                  }
                />
              </label>

              <label className="full">
                Sastojci
                <textarea
                  className="ap-input"
                  value={editRecipe.ingredients}
                  onChange={(e) =>
                    setEditRecipe({
                      ...editRecipe,
                      ingredients: e.target.value,
                    })
                  }
                />
              </label>

              <label className="full">
                Koraci pripreme
                <textarea
                  className="ap-input"
                  value={editRecipe.steps}
                  onChange={(e) =>
                    setEditRecipe({
                      ...editRecipe,
                      steps: e.target.value,
                    })
                  }
                />
              </label>

              <label className="ap-check">
                <input
                  type="checkbox"
                  checked={editRecipe.highlight}
                  onChange={(e) =>
                    setEditRecipe({
                      ...editRecipe,
                      highlight: e.target.checked,
                    })
                  }
                />
                Istaknuto
              </label>

              <label className="full">
                Postojeće slike
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "12px", marginTop: "8px" }}>
                  {editRecipe.recipe_images?.map((img) => (
                    <div key={img.id} style={{ position: "relative" }}>
                      <img
                        src={img.image_url}
                        alt=""
                        style={{
                          width: "100%",
                          height: "120px",
                          objectFit: "cover",
                          borderRadius: "8px",
                          border: "1px solid var(--border)",
                        }}
                      />
                      <button
                        type="button"
                        className="ap-btn danger small"
                        style={{
                          position: "absolute",
                          top: "4px",
                          right: "4px",
                          padding: "4px 8px",
                          fontSize: "0.75rem",
                        }}
                        onClick={() => deleteRecipeImage(img.id, img.image_url)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  {(!editRecipe.recipe_images || editRecipe.recipe_images.length === 0) && (
                    <div style={{ color: "var(--muted)", fontSize: "0.9rem" }}>
                      Nema slika
                    </div>
                  )}
                </div>
              </label>

              <label className="full">
                Dodaj nove slike
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) =>
                    setEditRecipeNewImages(Array.from(e.target.files || []))
                  }
                />
                {editRecipeNewImages.length > 0 && (
                  <span style={{ fontSize: "0.9rem", color: "var(--muted)", marginTop: "4px", display: "block" }}>
                    Odabrano: {editRecipeNewImages.length} nova slika
                  </span>
                )}
              </label>
            </div>

            <div className="ap-actions">
              <button
                className="ap-btn ghost"
                onClick={() => setEditRecipe(null)}
              >
                Odustani
              </button>
              <button className="ap-btn" onClick={saveRecipeEdit}>
                Spremi promjene
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TRAVEL MODAL */}
      {editTravel && (
        <div className="ap-modal">
          <div className="ap-modal-body ap-card">
            <div className="ap-modal-head">
              <h2>Uredi putovanje</h2>
              <button className="ap-x" onClick={() => {
                setEditTravel(null);
                setTravelEditTab("info");
                setTravelRestaurants([]);
              }}>
                ✕
              </button>
            </div>

            {/* TABS */}
            <div className="ap-tabs" style={{ marginBottom: "20px" }}>
              <button
                className={travelEditTab === "info" ? "active" : ""}
                onClick={() => setTravelEditTab("info")}
              >
                📝 Informacije
              </button>
              <button
                className={travelEditTab === "restaurants" ? "active" : ""}
                onClick={() => setTravelEditTab("restaurants")}
              >
                🍽️ Preporuke restorana ({travelRestaurants.length}/10)
              </button>
            </div>

            {/* INFO TAB */}
            {travelEditTab === "info" && (
              <div className="ap-form">
                <label>
                  Naslov
                  <input
                    className="ap-input"
                    value={editTravel.title}
                    onChange={(e) =>
                      setEditTravel({ ...editTravel, title: e.target.value })
                    }
                  />
                </label>

                <label>
                  Opis putovanja
                  <textarea
                    className="ap-input"
                    value={editTravel.description || ""}
                    onChange={(e) =>
                      setEditTravel({
                        ...editTravel,
                        description: e.target.value,
                      })
                    }
                  />
                </label>

                <label className="full">
                  Postojeće slike
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "12px", marginTop: "8px" }}>
                    {editTravel.travel_images?.map((img) => (
                      <div key={img.id} style={{ position: "relative" }}>
                        <img
                          src={img.image_url}
                          alt=""
                          style={{
                            width: "100%",
                            height: "120px",
                            objectFit: "cover",
                            borderRadius: "8px",
                            border: "1px solid var(--border)",
                          }}
                        />
                        <button
                          type="button"
                          className="ap-btn danger small"
                          style={{
                            position: "absolute",
                            top: "4px",
                            right: "4px",
                            padding: "4px 8px",
                            fontSize: "0.75rem",
                          }}
                          onClick={() => deleteTravelImage(img.id, img.image_url)}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    {editTravel.image_url && (!editTravel.travel_images || editTravel.travel_images.length === 0) && (
                      <div key="legacy" style={{ position: "relative" }}>
                        <img
                          src={editTravel.image_url}
                          alt=""
                          style={{
                            width: "100%",
                            height: "120px",
                            objectFit: "cover",
                            borderRadius: "8px",
                            border: "1px solid var(--border)",
                          }}
                        />
                        <div style={{ position: "absolute", bottom: "4px", left: "4px", background: "rgba(0,0,0,0.7)", color: "white", padding: "2px 6px", borderRadius: "4px", fontSize: "0.7rem" }}>
                          Stara slika
                        </div>
                      </div>
                    )}
                    {(!editTravel.travel_images || editTravel.travel_images.length === 0) && !editTravel.image_url && (
                      <div style={{ color: "var(--muted)", fontSize: "0.9rem" }}>
                        Nema slika
                      </div>
                    )}
                  </div>
                </label>

                <label className="full">
                  Dodaj nove slike
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) =>
                      setEditTravelNewImages(Array.from(e.target.files || []))
                    }
                  />
                  {editTravelNewImages.length > 0 && (
                    <span style={{ fontSize: "0.9rem", color: "var(--muted)", marginTop: "4px", display: "block" }}>
                      Odabrano: {editTravelNewImages.length} nova slika
                    </span>
                  )}
                </label>
              </div>
            )}

            {/* RESTAURANTS TAB */}
            {travelEditTab === "restaurants" && (
              <div className="ap-form">
                <div style={{ marginBottom: "24px", padding: "16px", background: "var(--bg-light)", borderRadius: "8px" }}>
                  <h3 style={{ marginTop: 0, marginBottom: "12px" }}>Dodaj novu preporuku restorana</h3>
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    <label>
                      Naziv restorana
                      <input
                        className="ap-input"
                        value={newRestaurantName}
                        onChange={(e) => setNewRestaurantName(e.target.value)}
                        placeholder="Unesite naziv restorana"
                        disabled={travelRestaurants.length >= 10}
                      />
                    </label>
                    <label>
                      Fotografija restorana
                      <input
                        ref={restaurantImageInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => setNewRestaurantImage(e.target.files?.[0] || null)}
                        disabled={travelRestaurants.length >= 10}
                      />
                      {newRestaurantImage && (
                        <span style={{ fontSize: "0.9rem", color: "var(--muted)", marginTop: "4px", display: "block" }}>
                          Odabrano: {newRestaurantImage.name}
                        </span>
                      )}
                    </label>
                    {travelRestaurants.length >= 10 && (
                      <p style={{ color: "#c33", fontSize: "0.9rem", margin: 0 }}>
                        Dosegnut je maksimum od 10 preporuka restorana
                      </p>
                    )}
                    <button
                      className="ap-btn"
                      onClick={addRestaurant}
                      disabled={travelRestaurants.length >= 10 || !newRestaurantName.trim() || !newRestaurantImage}
                    >
                      ➕ Dodaj preporuku
                    </button>
                  </div>
                </div>

                <label className="full">
                  Postojeće preporuke restorana ({travelRestaurants.length})
                  {travelRestaurants.length === 0 ? (
                    <div style={{ color: "var(--muted)", fontSize: "0.9rem", marginTop: "8px" }}>
                      Nema preporuka restorana
                    </div>
                  ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "16px", marginTop: "12px" }}>
                      {travelRestaurants.map((rest) => (
                        <div key={rest.id} style={{ position: "relative", border: "1px solid var(--border)", borderRadius: "8px", overflow: "hidden" }}>
                          <img
                            src={rest.image_url}
                            alt={rest.name}
                            style={{
                              width: "100%",
                              height: "150px",
                              objectFit: "cover",
                            }}
                          />
                          <div style={{ padding: "12px" }}>
                            <h4 style={{ margin: "0 0 8px 0", fontSize: "1rem" }}>{rest.name}</h4>
                            <button
                              type="button"
                              className="ap-btn danger small"
                              onClick={() => deleteRestaurant(rest.id, rest.image_url)}
                            >
                              Obriši
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </label>
              </div>
            )}

            <div className="ap-actions">
              <button
                className="ap-btn ghost"
                onClick={() => {
                  setEditTravel(null);
                  setTravelEditTab("info");
                  setTravelRestaurants([]);
                }}
              >
                Odustani
              </button>
              <button className="ap-btn" onClick={saveTravelEdit}>
                Spremi promjene
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
